<template>
    <div class="breadcrumb-container">
        <Button
            v-if="items.length > 1"
            icon="pi pi-chevron-left"
            class="up-button"
            text
            rounded
            @click="navigateUp"
        />
        <div v-else class="button-placeholder"></div>
        <ul class="breadcrumbs">
            <li><span class="crumb">&nbsp;</span></li>
            <li v-for="item in items" :key="item.description">
                <RouterLink
                    v-if="item.link && route.name !== item.link.name"
                    class="crumb"
                    :class="item.type"
                    :to="item.link"
                >
                    <span class="crumb-content">
                        <i :class="item.icon ? item.icon : 'fake-icon'"></i>
                        <span>{{ item.description }}</span>
                    </span>
                </RouterLink>
                <span
                    v-else
                    class="crumb"
                    :class="{
                        [item.type]: true,
                        current: route.name === item.link.name,
                    }"
                >
                    <span class="crumb-content">
                        <i :class="item.icon ? item.icon : 'fake-icon'"></i>
                        <span>{{ item.description }}</span>
                    </span>
                </span>
            </li>
        </ul>
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { RouterLink, useRouter } from "vue-router";
import type { RouteLocation } from "vue-router";
import { useRoute } from "vue-router";

import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import { ProcessElementType } from "@/lib/openapi/gen";
import { withThreadRevision } from "@/lib/thread-revision-link";
import { EntityType, getEntityInfo } from "@/stores/entity-store";
import { useCurrentEntityManager } from "@/stores/entity-store";

interface Crumb {
    icon?: string;
    description: string;
    link: RouteLocation;
    type: string;
}

const { organization } = useOrganizationManager();
const { stek } = useStekManager();

const {
    entity,
    domain,
    product,
    process,
    task,
    gateway,
    flow,
    event,
    artefact,
    role,
    businessEntity,
    domainTerm,
    application,
} = useCurrentEntityManager();

const route = useRoute();
const isLiveMode = computed(() => {
    return route.matched.find((m) => m.meta.mode)?.meta.mode === "live";
});
const isDevMode = computed(() => {
    return route.matched.find((m) => m.meta.mode)?.meta.mode === "dev";
});
const defaultParams = computed(() => ({
    orgName: organization.value?.name,
    stekName: stek.value?.name,
}));

const crumbs: {
    [key: string]: (id?: string, name?: string) => Crumb | undefined;
} = {
    applicationPage: (
        id = application.value?.identity,
        name = application.value?.name
    ) => {
        if (!id || !name) {
            return;
        }

        return {
            icon: "pi pi-server",
            description: name,
            link: withThreadRevision({
                name: "applicationPage",
                params: {
                    ...defaultParams.value,
                    applicationId: id,
                },
            }),
            type: "biz",
        };
    },
    applicationEntityPage: (id, name) => {
        // The route needs the parent application id; it comes from the current
        // route (always present on the application-entity page) or, when building
        // an ancestor crumb, from the resolved application in the entity chain.
        const applicationId = route.params.applicationId
            ? String(route.params.applicationId)
            : application.value?.identity;
        if (!id || !name || !applicationId) {
            return;
        }

        return {
            icon: "pi pi-table",
            description: name,
            link: withThreadRevision({
                name: "applicationEntityPage",
                params: {
                    ...defaultParams.value,
                    applicationId,
                    applicationEntityId: id,
                },
            }),
            type: "biz",
        };
    },
    domainPage: (id = domain.value?.identity, name = domain.value?.name) => {
        if (!id || !name) {
            return;
        }

        return {
            icon: "pi pi-cloud",
            description: name,
            link: withThreadRevision({
                name: isLiveMode.value
                    ? "liveDomainPage"
                    : isDevMode.value
                      ? "devDomainPage"
                      : "domainPage",
                params: {
                    ...defaultParams.value,
                    domainId: id,
                },
            }),
            type: "biz",
        };
    },
    productPage: (id = product.value?.identity, name = product.value?.name) => {
        if (!id || !name) {
            return;
        }

        return {
            icon: "pi pi-box",
            description: name,
            link: withThreadRevision({
                name: isLiveMode.value ? "liveProductPage" : "productPage",
                params: {
                    ...defaultParams.value,
                    productId: id,
                },
            }),
            type: "biz",
        };
    },
    rolePage: (id = role.value?.identity, name = role.value?.name) => {
        if (!id || !name) {
            return;
        }

        return {
            icon: "pi pi-id-card",
            description: name,
            link: withThreadRevision({
                name: "rolePage",
                params: {
                    ...defaultParams.value,
                    roleId: id,
                },
            }),
            type: "biz",
        };
    },
    businessEntityPage: (
        id = businessEntity.value?.identity,
        name = businessEntity.value?.name
    ) => {
        if (!id || !name) {
            return;
        }

        return {
            icon: "pi pi-building",
            description: name,
            link: withThreadRevision({
                name: "businessEntityPage",
                params: {
                    ...defaultParams.value,
                    businessEntityId: id,
                },
            }),
            type: "biz",
        };
    },
    domainTermPage: (
        id = domainTerm.value?.identity,
        name = domainTerm.value?.name
    ) => {
        if (!id || !name) {
            return;
        }

        return {
            icon: "pi pi-book",
            description: name,
            link: withThreadRevision({
                name: "domainTermPage",
                params: {
                    ...defaultParams.value,
                    domainTermId: id,
                },
            }),
            type: "biz",
        };
    },
    processPage: (id = process.value?.identity, name = process.value?.name) => {
        if (!id || !name) {
            return;
        }

        return {
            icon: "pi pi-share-alt",
            description: name,
            link: withThreadRevision({
                ...defaultParams.value,
                name: isLiveMode.value ? "liveProcessPage" : "processPage",
                params: {
                    ...defaultParams.value,
                    processId: id,
                },
            }),
            type: "biz",
        };
    },
    taskPage: (
        id = task.value?.identity,
        name = task.value?.name || task.value?.bpmn_id
    ) => {
        if (!id || !name) {
            return;
        }

        return {
            icon: "pi pi-check-square",
            description: name,
            link: withThreadRevision({
                name: "processElementPage",
                params: {
                    ...defaultParams.value,
                    elementId: id,
                    elementType: ProcessElementType.TASK,
                },
            }),
            type: "biz",
        };
    },
    gatewayPage: (
        id = gateway.value?.identity,
        name = gateway.value?.name || gateway.value?.bpmn_id
    ) => {
        if (!id || !name) {
            return;
        }

        return {
            icon: "pi pi-directions",
            description: name,
            link: withThreadRevision({
                name: "processElementPage",
                params: {
                    ...defaultParams.value,
                    elementId: id,
                    elementType: ProcessElementType.GATEWAY,
                },
            }),
            type: "biz",
        };
    },
    eventPage: (
        id = event.value?.identity,
        name = event.value?.name || event.value?.bpmn_id
    ) => {
        if (!id || !name) {
            return;
        }

        return {
            icon: "pi pi-stop-circle",
            description: name,
            link: withThreadRevision({
                name: "processElementPage",
                params: {
                    ...defaultParams.value,
                    elementId: id,
                    elementType: ProcessElementType.EVENT,
                },
            }),
            type: "biz",
        };
    },
    flowPage: (
        id = flow.value?.identity,
        name = flow.value?.name || flow.value?.bpmn_id
    ) => {
        if (!id || !name) {
            return;
        }

        return {
            icon: "pi pi-window-minimize",
            description: name,
            link: withThreadRevision({
                name: "processElementPage",
                params: {
                    ...defaultParams.value,
                    elementId: id,
                    elementType: ProcessElementType.FLOW,
                },
            }),
            type: "biz",
        };
    },
    documentPage: (
        id = artefact.value?.identity,
        name = artefact.value?.name
    ) => {
        if (!id || !name) {
            return;
        }

        return {
            icon: "pi pi-file",
            description: name || "Document",
            link: withThreadRevision({
                name: route.name,
                params: {
                    artefactId: id,
                },
            }),
            type: "biz",
        };
    },
    processDefinitionPage: (id = process.value?.identity, name = "Diagram") => {
        if (!id || !name) {
            return;
        }

        return {
            description: name,
            link: withThreadRevision({
                name: "processDefinitionPage",
                params: {
                    ...defaultParams.value,
                    processId: id,
                },
            }),
            type: "biz",
        };
    },
    processDefinitionHistoryPage: (
        id = process.value?.identity,
        name = "Diagram"
    ) => {
        if (!id || !name) {
            return;
        }

        return {
            icon: "pi pi-history",
            description: name,
            link: withThreadRevision({
                name: "processDefinitionHistoryPage",
                params: {
                    ...defaultParams.value,
                    processId: process.value.identity,
                },
            }),
            type: "biz",
        };
    },
};

type EntityTypeToCrumb = {
    [key in EntityType]?: string;
};

const entityTypeToCrumb: EntityTypeToCrumb = {
    domain: "domainPage",
    product: "productPage",
    role: "rolePage",
    business_entity: "businessEntityPage",
    domain_term: "domainTermPage",
    process: "processPage",
    application: "applicationPage",
    application_entity: "applicationEntityPage",
    task: "taskPage",
    event: "eventPage",
    gateway: "gatewayPage",
    flow: "flowPage",
    artefact: "documentPage",
};

// Crumbs that don't have a corresponding entity (perhaps they can be represented
// through a specific entity + attribute combination in the future)
const specialCrumbs = {
    process_definition: "processDefinitionPage",
};

const router = useRouter();

const hasValidParams = computed(() =>
    Boolean(organization.value?.name && stek.value?.name)
);

const navigateUp = () => {
    if (items.value.length > 1) {
        const target = items.value[items.value.length - 2];
        router.push(target.link);
    }
};

const stekCrumb = computed<Crumb | undefined>(() => {
    if (!stek.value?.name || !hasValidParams.value) {
        return undefined;
    }
    return {
        icon: "pi pi-building",
        description: stek.value.name,
        // Stay in the current mode's stek-level page: the IT Landscape (dev) and
        // the live overview both have their own stek route — only fall back to the
        // business stek page when in neither.
        link: withThreadRevision({
            name: isLiveMode.value
                ? "livePage"
                : isDevMode.value
                  ? "devPage"
                  : "stekPage",
            params: {
                orgName: organization.value!.name,
                stekName: stek.value.name,
            },
        }),
        type: "biz",
    };
});

const items = computed<Crumb[]>(() => {
    // On the strategy section the crumb is the section itself, not the stek.
    if (
        String(route.name ?? "").startsWith("strategy") &&
        organization.value?.name &&
        stek.value?.name
    ) {
        return [
            {
                icon: "pi pi-compass",
                description: "Strategy",
                link: {
                    name: "strategyVision",
                    params: {
                        orgName: organization.value.name,
                        stekName: stek.value.name,
                    },
                },
                type: "strategy",
            },
        ];
    }
    if ("breadcrumbs" in route.meta && route.meta.breadcrumbs) {
        if (route.meta.breadcrumbs === "auto") {
            // Iterate through entity's ancestors to generate breadcrumbs
            let currentEntity = entity.value;
            const generatedCrumbs: Crumb[] = [];

            // Some pages have a special breadcrumb which doesn't directly correspond to an entity while still being
            // part of an entity's hierarchy.
            if (
                Object.values(specialCrumbs).find((name) => name === route.name)
            ) {
                const entityInfo = getEntityInfo(route.params);
                if (entityInfo?.entityId) {
                    const crumbFn = crumbs[String(route.name)];
                    const crumb = crumbFn(String(entityInfo.entityId));
                    if (crumb) {
                        generatedCrumbs.push(crumb);
                    }
                }
            }
            while (currentEntity) {
                const crumbName = entityTypeToCrumb[currentEntity.entity_type];
                const crumbFn = crumbs[String(crumbName)];
                if (crumbFn) {
                    const crumb = crumbFn(
                        currentEntity.identity,
                        currentEntity.name
                    );
                    if (crumb) {
                        generatedCrumbs.push(crumb);
                    } else {
                        throw new Error(
                            `Could not generate breadcrumb for ${currentEntity.entity_type} with id ${currentEntity.identity}`
                        );
                    }
                }
                currentEntity = currentEntity.parent;
            }

            if (stekCrumb.value) {
                generatedCrumbs.push(stekCrumb.value);
            }

            return generatedCrumbs.toReversed();
        } else {
            const explicitCrumbs = route.meta.breadcrumbs
                .map((key: string) => crumbs[key]())
                .filter((c) => c !== undefined); // Filter out undefined values
            return stekCrumb.value
                ? [stekCrumb.value, ...explicitCrumbs]
                : explicitCrumbs;
        }
    }
    return stekCrumb.value ? [stekCrumb.value] : [];
});
</script>

<style scoped lang="scss">
.button-placeholder {
    width: 2.5rem; /* Same width as the button */
    height: 2.5rem; /* Same height as the button */
}

.breadcrumb-container {
    display: flex;
    align-items: center;
}

.up-button {
    z-index: 1;
}

.breadcrumbs {
    display: flex;
    position: relative;
    list-style: none;
    overflow: hidden;
    left: -5px;
    z-index: 0;

    li {
        float: left;

        .crumb {
            /* 30px: the width of the colored triangle
                5px: the width of the white triangle
                2px: probably some inset */
            left: calc(-30px + 5px + 2px);
            height: 100%;
            color: white;
            text-decoration: none;
            padding: 5px 0 5px 30px;
            background: grey;
            position: relative;
            display: block;
            float: left;

            // Ensure text is visible in LIVE mode
            .crumb-content {
                color: white !important;
            }

            &::before {
                content: " ";
                display: block;
                width: 0;
                height: 0;
                border-top: 50px solid transparent;
                border-bottom: 50px solid transparent;
                border-left: 30px solid var(--p-menubar-background);
                position: absolute;
                top: 50%;
                margin-top: -50px;
                /* The width of the white triangle */
                margin-left: 5px;
                left: calc(100% - 15px);
                z-index: 1;
            }

            &::after {
                content: " ";
                display: block;
                width: 0;
                height: 0;
                border-top: 50px solid transparent;
                border-bottom: 50px solid transparent;
                border-left: 30px solid grey;
                position: absolute;
                top: 50%;
                margin-top: -50px;
                left: calc(100% - 15px);
                z-index: 2;
            }

            &:not(.current) {
                &.biz {
                    background: var(--stekz-primary-color);

                    &::after {
                        border-left-color: var(--stekz-primary-color);
                    }
                }
            }

            // Accent persists even on the current route.
            &.strategy {
                background: var(--stekz-primary-color);

                &::after {
                    border-left-color: var(--stekz-primary-color);
                }
            }

            // Ensure text is visible in LIVE mode with purple background
            .crumb-content {
                color: white !important;

                span {
                    color: white !important;
                }

                i {
                    color: white !important;
                }
            }
        }
    }

    li:first-child {
        .crumb {
            padding: 5px 0 5px 10px;
        }

        /* To make the colored arrow disappear for the first fake item in the breadcrumb */
        .crumb::after {
            content: none;
        }
    }

    .crumb-content {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        position: relative;
        z-index: 3;
        white-space: nowrap;

        .fake-icon {
            height: 14px;
        }
    }
}
</style>
